import {
  CarHandTypeSearchOption,
  CarManufacturerDto,
  SearchMetadataResponse,
  VehicleSingleValuedClassificationModel,
  VehicleTagModel,
  VehicleTypeModel,
} from "api"
import { CarType, CarTypeId, CarTypeToClassificationCodes } from "./carTypeId"

/** Facets for a category that has no inventory at all. */
export const emptySearchMetadata: SearchMetadataResponse = {
  tags: [],
  types: [],
  vehicleSingleValuedClassifications: [],
  manufacturers: [],
  minimumPrice: 0,
  maximumPrice: 9999999,
  hands: [],
}

/**
 * Builds the per-category facets for one deal-type context from a grouped
 * metadata body.
 *
 * Sale slices are keyed by deal type ("new:classificationN" / "used:classificationN");
 * leasing slices keep the bare "classificationN" key (prefix = "").
 */
export function buildClassificationMetadata(
  body: Record<string, SearchMetadataResponse> | null | undefined,
  prefix: string,
): Record<CarType, SearchMetadataResponse> {
  const slice = (code: CarTypeId) => body?.[`${prefix}classification${code}`]

  return {
    Premium: mergeSearchMetadata(
      CarTypeToClassificationCodes.Premium.map(slice),
    ),
    Standard: mergeSearchMetadata(
      CarTypeToClassificationCodes.Standard.map(slice),
    ),
  }
}

/** Merge manufacturers by code, unioning their models by modelCode. */
function mergeManufacturers(
  groups: CarManufacturerDto[][],
): CarManufacturerDto[] {
  const byCode = new Map<number, CarManufacturerDto>()

  groups.flat().forEach((manufacturer) => {
    const existing = byCode.get(manufacturer.code)
    if (!existing) {
      byCode.set(manufacturer.code, {
        ...manufacturer,
        models: [...(manufacturer.models || [])],
      })
      return
    }
    const seenModels = new Set(existing.models.map((model) => model.modelCode))
    ;(manufacturer.models || []).forEach((model) => {
      if (!seenModels.has(model.modelCode)) {
        seenModels.add(model.modelCode)
        existing.models.push(model)
      }
    })
  })

  return Array.from(byCode.values())
}

/**
 * Merge facet entries (tags / types / hands) by their identifying key, unioning
 * the manufacturers attached to each entry. The manufacturer lists matter: the
 * search page intersects them to work out which brands remain selectable.
 */
function mergeFacets<T extends { manufacturers: CarManufacturerDto[] }>(
  groups: T[][],
  getKey: (item: T) => string | number | null,
): T[] {
  const byKey = new Map<string | number | null, T>()

  groups.flat().forEach((item) => {
    const key = getKey(item)
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, {
        ...item,
        manufacturers: [...(item.manufacturers || [])],
      })
      return
    }
    existing.manufacturers = mergeManufacturers([
      existing.manufacturers,
      item.manufacturers || [],
    ])
  })

  return Array.from(byKey.values())
}

/** A fresh, independent copy of the no-inventory facets. */
function freshEmptyMetadata(): SearchMetadataResponse {
  return {
    ...emptySearchMetadata,
    tags: [],
    types: [],
    vehicleSingleValuedClassifications: [],
    manufacturers: [],
    hands: [],
  }
}

/**
 * A slice carries no usable price signal when its upper bound is 0. The API
 * emits these for categories it has no pricing for (live leasing metadata
 * reports `classification3` as 0..0), and folding one into the bounds would
 * collapse the budget floor to ₪0 while contributing nothing to the ceiling.
 */
function hasPriceSignal(slice: SearchMetadataResponse): boolean {
  return slice.maximumPrice > 0
}

/**
 * Merges the API's per-classification metadata slices into the facets for one
 * search category. Used to build the "פרימיום" category from classification 2
 * and 3 (END-86).
 *
 * Slices absent from the API response are skipped rather than substituted with
 * an empty placeholder — folding a placeholder in would drag `minimumPrice` to
 * 0 and `maximumPrice` to the 9999999 fallback, widening the budget slider well
 * past the real inventory. Facets are merged from every present slice, but the
 * price bounds are taken only from slices that actually carry pricing.
 */
export function mergeSearchMetadata(
  slices: (SearchMetadataResponse | null | undefined)[],
): SearchMetadataResponse {
  const present = slices.filter(
    (slice): slice is SearchMetadataResponse => !!slice,
  )

  if (present.length === 0) return freshEmptyMetadata()

  const classifications = new Map<
    number,
    VehicleSingleValuedClassificationModel
  >()
  present
    .flatMap((slice) => slice.vehicleSingleValuedClassifications || [])
    .forEach((classification) => {
      classifications.set(
        classification.vehicleSingleValuedClassificationCode,
        classification,
      )
    })

  // Fall back to every slice when none carries pricing, so a single 0..0
  // category keeps reporting its own bounds instead of the 9999999 fallback.
  const priced = present.filter(hasPriceSignal)
  const boundsFrom = priced.length > 0 ? priced : present

  return {
    tags: mergeFacets<VehicleTagModel>(
      present.map((slice) => slice.tags || []),
      (tag) => tag.vehicleTagForWebsiteCode,
    ),
    types: mergeFacets<VehicleTypeModel>(
      present.map((slice) => slice.types || []),
      (type) => type.vehicleTypeForWebsiteCode,
    ),
    vehicleSingleValuedClassifications: Array.from(classifications.values()),
    manufacturers: mergeManufacturers(
      present.map((slice) => slice.manufacturers || []),
    ),
    minimumPrice: Math.min(...boundsFrom.map((slice) => slice.minimumPrice)),
    maximumPrice: Math.max(...boundsFrom.map((slice) => slice.maximumPrice)),
    hands: mergeFacets<CarHandTypeSearchOption>(
      present.map((slice) => slice.hands || []),
      (hand) => hand.key,
    ),
  }
}
